/**
 * Auto-sync manager for handling automatic synchronization timers
 */

import { CorporateSyncManager } from './CorporateSyncManager';
import { PersonalSyncManager } from './PersonalSyncManager';
import { InternalSyncManager } from './InternalSyncManager';

export class AutoSyncManager {
  private corporateSyncManager: CorporateSyncManager;
  private personalSyncManager: PersonalSyncManager;
  private internalSyncManager: InternalSyncManager;
  
  public syncInterval: NodeJS.Timeout | null = null;
  public personalSyncInterval: NodeJS.Timeout | null = null;
  public internalSyncInterval: NodeJS.Timeout | null = null;
  private consecutiveFailures = 0;
  private personalConsecutiveFailures = 0;
  private internalConsecutiveFailures = 0;
  private maxConsecutiveFailures = 3;

  constructor(
    corporateSyncManager: CorporateSyncManager,
    personalSyncManager: PersonalSyncManager,
    internalSyncManager: InternalSyncManager
  ) {
    this.corporateSyncManager = corporateSyncManager;
    this.personalSyncManager = personalSyncManager;
    this.internalSyncManager = internalSyncManager;
  }

  /**
   * Start automatic sync every configured interval
   */
  startAutoSync(accessToken: string, sharedUpn: string, getTokenCallback?: () => Promise<string>): void {
    console.log('startAutoSync called with:', {
      hasAccessToken: !!accessToken,
      sharedUpn,
      hasTokenCallback: !!getTokenCallback
    });
    
    this.stopAutoSync(); // Clear any existing interval

    const intervalMinutes = this.getSyncIntervalMs() / (60 * 1000);
    console.log(`Starting auto-sync with ${intervalMinutes}-minute interval...`);
    
    try {
      this.syncInterval = setInterval(async () => {
        console.log('Auto-sync timer triggered - starting incremental sync...');
        try {
          // Get fresh token if callback is provided
          const token = getTokenCallback ? await getTokenCallback() : accessToken;
          console.log('Auto-sync using token:', token ? 'Fresh token obtained' : 'Using original token');
          
          // Check if bootstrap is complete before attempting incremental sync
          const meta = await this.corporateSyncManager.getStore().getMeta();
          if (!meta.bootstrapComplete) {
            console.log('Bootstrap not complete yet, skipping auto-sync');
            return;
          }
          
          await this.corporateSyncManager.incrementalSync(token, sharedUpn);
          this.consecutiveFailures = 0; // Reset on success
          console.log('Auto-sync completed successfully');
        } catch (error) {
          this.consecutiveFailures++;
          console.error(`Auto sync failed (${this.consecutiveFailures}/${this.maxConsecutiveFailures}):`, error);
          
          // Stop auto sync after too many consecutive failures
          if (this.consecutiveFailures >= this.maxConsecutiveFailures) {
            console.error('Too many consecutive sync failures, stopping auto sync');
            this.stopAutoSync();
          }
        }
      }, this.getSyncIntervalMs());
      
      console.log('Auto-sync timer started with ID:', this.syncInterval);
    } catch (error) {
      console.error('Failed to start auto-sync timer:', error);
    }
  }

  /**
   * Stop automatic sync
   */
  stopAutoSync(): void {
    if (this.syncInterval) {
      console.log('Stopping auto-sync timer...');
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
    this.consecutiveFailures = 0; // Reset failure count
  }

  /**
   * Start automatic sync for personal contacts every configured interval
   */
  startPersonalDeltaSync(accessToken: string, userUpn: string, getTokenCallback?: () => Promise<string>): void {
    console.log('startPersonalDeltaSync called with:', {
      hasAccessToken: !!accessToken,
      userUpn,
      hasTokenCallback: !!getTokenCallback
    });
    
    this.stopPersonalDeltaSync(); // Clear any existing interval

    const intervalMinutes = this.getSyncIntervalMs() / (60 * 1000);
    console.log(`Starting personal delta sync with ${intervalMinutes}-minute interval...`);
    
    try {
      this.personalSyncInterval = setInterval(async () => {
        console.log('Personal delta sync timer triggered - starting incremental sync...');
        try {
          // Get fresh token if callback is provided
          const token = getTokenCallback ? await getTokenCallback() : accessToken;
          console.log('Personal delta sync using token:', token ? 'Fresh token obtained' : 'Using original token');
          
          // Check if personal bootstrap is complete before attempting incremental sync
          const meta = await this.personalSyncManager.getStore().getMeta();
          if (!meta.personalBootstrapComplete) {
            console.log('Personal bootstrap not complete yet, skipping delta sync');
            return;
          }
          
          await this.personalSyncManager.incrementalPersonalSync(token, userUpn);
          this.personalConsecutiveFailures = 0; // Reset on success
          console.log('Personal delta sync completed successfully');
        } catch (error) {
          this.personalConsecutiveFailures++;
          console.error(`Personal delta sync failed (${this.personalConsecutiveFailures}/${this.maxConsecutiveFailures}):`, error);
          
          // Stop auto sync after too many consecutive failures
          if (this.personalConsecutiveFailures >= this.maxConsecutiveFailures) {
            console.error('Too many consecutive personal sync failures, stopping personal delta sync');
            this.stopPersonalDeltaSync();
          }
        }
      }, this.getSyncIntervalMs());
      
      console.log('Personal delta sync timer started with ID:', this.personalSyncInterval);
    } catch (error) {
      console.error('Failed to start personal delta sync timer:', error);
    }
  }

  /**
   * Stop automatic sync for personal contacts
   */
  stopPersonalDeltaSync(): void {
    if (this.personalSyncInterval) {
      console.log('Stopping personal delta sync timer...');
      clearInterval(this.personalSyncInterval);
      this.personalSyncInterval = null;
    }
    this.personalConsecutiveFailures = 0; // Reset failure count
  }

  /**
   * Start automatic sync for internal users every configured interval
   */
  startInternalAutoSync(accessToken: string, getTokenCallback?: () => Promise<string>): void {
    console.log('startInternalAutoSync called with:', {
      hasAccessToken: !!accessToken,
      hasTokenCallback: !!getTokenCallback
    });
    
    this.stopInternalAutoSync(); // Clear any existing interval

    const intervalMinutes = this.getSyncIntervalMs() / (60 * 1000);
    console.log(`Starting internal auto-sync with ${intervalMinutes}-minute interval...`);
    
    try {
      this.internalSyncInterval = setInterval(async () => {
        console.log('Internal auto-sync timer triggered - starting incremental sync...');
        try {
          // Get fresh token if callback is provided
          const token = getTokenCallback ? await getTokenCallback() : accessToken;
          console.log('Internal auto-sync using token:', token ? 'Fresh token obtained' : 'Using original token');
          await this.internalSyncManager.incrementalInternalSync(token);
          this.internalConsecutiveFailures = 0; // Reset on success
          console.log('Internal auto-sync completed successfully');
        } catch (error) {
          this.internalConsecutiveFailures++;
          console.error(`Internal auto sync failed (${this.internalConsecutiveFailures}/${this.maxConsecutiveFailures}):`, error);
          
          // Stop auto sync after too many consecutive failures
          if (this.internalConsecutiveFailures >= this.maxConsecutiveFailures) {
            console.error('Too many consecutive internal sync failures, stopping internal auto sync');
            this.stopInternalAutoSync();
          }
        }
      }, this.getSyncIntervalMs());
      
      console.log('Internal auto-sync timer started with ID:', this.internalSyncInterval);
    } catch (error) {
      console.error('Failed to start internal auto-sync timer:', error);
    }
  }

  /**
   * Stop automatic sync for internal users
   */
  stopInternalAutoSync(): void {
    if (this.internalSyncInterval) {
      console.log('Stopping internal auto-sync timer...');
      clearInterval(this.internalSyncInterval);
      this.internalSyncInterval = null;
    }
    this.internalConsecutiveFailures = 0; // Reset failure count
  }

  /**
   * Get sync interval in milliseconds from environment variable
   */
  public getSyncIntervalMs(): number {
    const syncIntervalMinutes = process.env.SYNC_INTERVAL;
    if (syncIntervalMinutes) {
      const minutes = parseInt(syncIntervalMinutes, 10);
      if (!isNaN(minutes) && minutes > 0) {
        console.log(`Using sync interval from environment: ${minutes} minutes`);
        return minutes * 60 * 1000; // Convert minutes to milliseconds
      }
    }
    
    // Default to 15 minutes if not set or invalid
    console.log('Using default sync interval: 15 minutes');
    return 15 * 60 * 1000;
  }

  /**
   * Check auto-sync status and log details
   */
  checkAutoSyncStatus(): void {
    console.log('Auto-sync status check:', {
      hasInterval: !!this.syncInterval,
      intervalId: this.syncInterval,
      consecutiveFailures: this.consecutiveFailures,
      maxFailures: this.maxConsecutiveFailures,
      syncIntervalMinutes: this.getSyncIntervalMs() / (60 * 1000)
    });
  }

  /**
   * Check internal auto-sync status and log details
   */
  checkInternalAutoSyncStatus(): void {
    console.log('Internal auto-sync status check:', {
      hasInterval: !!this.internalSyncInterval,
      intervalId: this.internalSyncInterval,
      consecutiveFailures: this.internalConsecutiveFailures,
      maxFailures: this.maxConsecutiveFailures,
      syncIntervalMinutes: this.getSyncIntervalMs() / (60 * 1000)
    });
  }

  /**
   * Manually trigger auto-sync (for testing)
   */
  async triggerAutoSync(accessToken: string, sharedUpn: string): Promise<void> {
    console.log('Manually triggering auto-sync...');
    try {
      await this.corporateSyncManager.incrementalSync(accessToken, sharedUpn);
      console.log('Manual auto-sync completed successfully');
    } catch (error) {
      console.error('Manual auto-sync failed:', error);
      throw error;
    }
  }

  /**
   * Manually trigger internal auto-sync (for testing)
   */
  async triggerInternalAutoSync(accessToken: string): Promise<void> {
    console.log('Manually triggering internal auto-sync...');
    try {
      await this.internalSyncManager.incrementalInternalSync(accessToken);
      console.log('Manual internal auto-sync completed successfully');
    } catch (error) {
      console.error('Manual internal auto-sync failed:', error);
      throw error;
    }
  }

  /**
   * Check if incremental sync is needed and perform it if necessary
   */
  async checkAndPerformStartupSync(
    accessToken: string, 
    sharedUpn: string, 
    getTokenCallback?: () => Promise<string>
  ): Promise<boolean> {
    console.log('Checking if startup sync is needed...');
    
    try {
      const meta = await this.corporateSyncManager.getStore().getMeta();
      
      // If bootstrap is not complete, don't do startup sync
      if (!meta.bootstrapComplete) {
        console.log('Bootstrap not complete, skipping startup sync');
        return false;
      }
      
      // If no delta token, don't do startup sync
      if (!meta.deltaToken) {
        console.log('No delta token available, skipping startup sync');
        return false;
      }
      
      // Check if last sync (incremental or full) is older than the configured sync interval
      const lastIncrementalSync = meta.lastIncrementalSync;
      const lastFullSync = meta.lastFullSync;
      const syncThreshold = this.getSyncIntervalMs();
      
      // Determine the most recent sync time
      let lastSyncTime: number | null = null;
      let lastSyncType = '';
      
      if (lastIncrementalSync && lastFullSync) {
        const incrementalTime = new Date(lastIncrementalSync).getTime();
        const fullTime = new Date(lastFullSync).getTime();
        if (incrementalTime > fullTime) {
          lastSyncTime = incrementalTime;
          lastSyncType = 'incremental';
        } else {
          lastSyncTime = fullTime;
          lastSyncType = 'full';
        }
      } else if (lastIncrementalSync) {
        lastSyncTime = new Date(lastIncrementalSync).getTime();
        lastSyncType = 'incremental';
      } else if (lastFullSync) {
        lastSyncTime = new Date(lastFullSync).getTime();
        lastSyncType = 'full';
      }
      
      if (lastSyncTime) {
        const now = Date.now();
        const timeSinceLastSync = now - lastSyncTime;
        
        console.log(`Last ${lastSyncType} sync:`, {
          lastSyncTime: new Date(lastSyncTime).toISOString(),
          timeSinceLastSync: Math.round(timeSinceLastSync / 1000) + ' seconds',
          threshold: Math.round(syncThreshold / 1000) + ' seconds',
          needsSync: timeSinceLastSync > syncThreshold
        });
        
        if (timeSinceLastSync <= syncThreshold) {
          console.log('Last sync is recent, skipping startup sync');
          // Still start auto-sync even if no startup sync is needed
          console.log('Starting auto-sync (no startup sync needed)...');
          this.startAutoSync(accessToken, sharedUpn, getTokenCallback);
          return false;
        }
      } else {
        // No previous sync found - start auto-sync without doing startup sync
        console.log('No previous sync found, starting auto-sync...');
        this.startAutoSync(accessToken, sharedUpn, getTokenCallback);
        return false;
      }
      
      console.log('Startup sync needed, performing incremental sync...');
      
      // Perform incremental sync
      await this.corporateSyncManager.incrementalSync(accessToken, sharedUpn);
      
      // Start auto-sync
      console.log('Starting auto-sync after startup sync...');
      this.startAutoSync(accessToken, sharedUpn, getTokenCallback);
      
      console.log('Startup sync completed successfully');
      return true;
      
    } catch (error) {
      console.error('Startup sync failed:', error);
      return false;
    }
  }

  /**
   * Check if personal contacts incremental sync is needed and perform it if necessary
   */
  async checkAndPerformPersonalStartupSync(
    accessToken: string, 
    userUpn: string, 
    getTokenCallback?: () => Promise<string>
  ): Promise<boolean> {
    console.log('Checking if personal startup sync is needed...');
    
    try {
      const meta = await this.personalSyncManager.getStore().getMeta();
      
      // If personal bootstrap is not complete, don't do startup sync
      if (!meta.personalBootstrapComplete) {
        console.log('Personal bootstrap not complete, skipping startup sync');
        return false;
      }
      
      // If no personal delta token, don't do startup sync
      if (!meta.personalDeltaToken) {
        console.log('No personal delta token available, skipping startup sync');
        return false;
      }
      
      // Check if last personal sync (incremental or full) is older than the configured sync interval
      const lastPersonalIncrementalSync = meta.personalLastIncrementalSync;
      const lastPersonalFullSync = meta.personalLastFullSync;
      const syncThreshold = this.getSyncIntervalMs();
      
      // Determine the most recent personal sync time
      let lastSyncTime: number | null = null;
      let lastSyncType = '';
      
      if (lastPersonalIncrementalSync && lastPersonalFullSync) {
        const incrementalTime = new Date(lastPersonalIncrementalSync).getTime();
        const fullTime = new Date(lastPersonalFullSync).getTime();
        if (incrementalTime > fullTime) {
          lastSyncTime = incrementalTime;
          lastSyncType = 'incremental';
        } else {
          lastSyncTime = fullTime;
          lastSyncType = 'full';
        }
      } else if (lastPersonalIncrementalSync) {
        lastSyncTime = new Date(lastPersonalIncrementalSync).getTime();
        lastSyncType = 'incremental';
      } else if (lastPersonalFullSync) {
        lastSyncTime = new Date(lastPersonalFullSync).getTime();
        lastSyncType = 'full';
      }
      
      if (lastSyncTime) {
        const now = Date.now();
        const timeSinceLastSync = now - lastSyncTime;
        
        console.log(`Last personal ${lastSyncType} sync:`, {
          lastSyncTime: new Date(lastSyncTime).toISOString(),
          timeSinceLastSync: Math.round(timeSinceLastSync / 1000) + ' seconds',
          threshold: Math.round(syncThreshold / 1000) + ' seconds',
          needsSync: timeSinceLastSync > syncThreshold
        });
        
        if (timeSinceLastSync <= syncThreshold) {
          console.log('Last personal sync is recent, skipping startup sync');
          return false;
        }
      } else {
        // No previous personal sync found
        console.log('No previous personal sync found, skipping startup sync');
        return false;
      }
      
      console.log('Personal startup sync needed, performing incremental sync...');
      
      // Perform incremental sync for personal contacts
      await this.personalSyncManager.incrementalPersonalSync(accessToken, userUpn);
      
      console.log('Personal startup sync completed successfully');
      return true;
      
    } catch (error) {
      console.error('Personal startup sync failed:', error);
      return false;
    }
  }

  /**
   * Check if internal users incremental sync is needed and perform it if necessary
   */
  async checkAndPerformInternalStartupSync(
    accessToken: string, 
    getTokenCallback?: () => Promise<string>
  ): Promise<boolean> {
    console.log('Checking if internal startup sync is needed...');
    
    try {
      const meta = await this.internalSyncManager.getStore().getMeta();
      
      // If internal bootstrap is not complete, don't do startup sync
      if (!meta.internalBootstrapComplete) {
        console.log('Internal bootstrap not complete, skipping startup sync');
        return false;
      }
      
      // If no internal delta token, don't do startup sync
      if (!meta.internalDeltaToken) {
        console.log('No internal delta token available, skipping startup sync');
        return false;
      }
      
      // Check if last internal sync (incremental or full) is older than the configured sync interval
      const lastInternalIncrementalSync = meta.internalLastIncrementalSync;
      const lastInternalFullSync = meta.internalLastFullSync;
      const syncThreshold = this.getSyncIntervalMs();
      
      // Determine the most recent internal sync time
      let lastSyncTime: number | null = null;
      let lastSyncType = '';
      
      if (lastInternalIncrementalSync && lastInternalFullSync) {
        const incrementalTime = new Date(lastInternalIncrementalSync).getTime();
        const fullTime = new Date(lastInternalFullSync).getTime();
        if (incrementalTime > fullTime) {
          lastSyncTime = incrementalTime;
          lastSyncType = 'incremental';
        } else {
          lastSyncTime = fullTime;
          lastSyncType = 'full';
        }
      } else if (lastInternalIncrementalSync) {
        lastSyncTime = new Date(lastInternalIncrementalSync).getTime();
        lastSyncType = 'incremental';
      } else if (lastInternalFullSync) {
        lastSyncTime = new Date(lastInternalFullSync).getTime();
        lastSyncType = 'full';
      }
      
      if (lastSyncTime) {
        const now = Date.now();
        const timeSinceLastSync = now - lastSyncTime;
        
        console.log(`Last internal ${lastSyncType} sync:`, {
          lastSyncTime: new Date(lastSyncTime).toISOString(),
          timeSinceLastSync: Math.round(timeSinceLastSync / 1000) + ' seconds',
          threshold: Math.round(syncThreshold / 1000) + ' seconds',
          needsSync: timeSinceLastSync > syncThreshold
        });
        
        if (timeSinceLastSync <= syncThreshold) {
          console.log('Last internal sync is recent, skipping startup sync');
          // Still start auto-sync even if no startup sync is needed
          console.log('Starting internal auto-sync (no startup sync needed)...');
          this.startInternalAutoSync(accessToken, getTokenCallback);
          return false;
        }
      } else {
        // No previous internal sync found - start auto-sync without doing startup sync
        console.log('No previous internal sync found, starting internal auto-sync...');
        this.startInternalAutoSync(accessToken, getTokenCallback);
        return false;
      }
      
      console.log('Internal startup sync needed, performing incremental sync...');
      
      // Perform incremental sync
      await this.internalSyncManager.incrementalInternalSync(accessToken);
      
      // Start auto-sync
      console.log('Starting internal auto-sync after startup sync...');
      this.startInternalAutoSync(accessToken, getTokenCallback);
      
      console.log('Internal startup sync completed successfully');
      return true;
      
    } catch (error) {
      console.error('Internal startup sync failed:', error);
      return false;
    }
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.stopAutoSync();
    this.stopPersonalDeltaSync();
    this.stopInternalAutoSync();
  }
}
